/**
 * The channel console (web-plan.md F6, D144/D70): draft-then-Done, the
 * desktop's `channel_settings.rs` section for section. Nothing writes until
 * Done; Done writes in the ported order — backgrounds first (upload the
 * picked files, one PUT with both halves), then one PATCH with the changed
 * scalars — because a PATCH that lands before a failed picture would
 * silently half-save (runtime/channel_settings.rs:175).
 *
 */
import { For, type JSX, Show, createSignal, onMount } from "solid-js";

import { escapable } from "../lib/escape";
import { currentLifetime, isCurrent, assertCurrent } from "../lib/lifetime";
import * as api from "../lib/api/rest";
import { upload } from "../lib/api/uploads";
import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import type { Runtime } from "../store/runtime";
import type { Room } from "../store/types";
import { ActivityIndicator, Icon } from "../components/ds";
import type { ChannelMember, Invite } from "../gen/dto";

type BgIntent = { t: "keep" } | { t: "remove" } | { t: "pick"; file: File };

const SLOWMODE_STEPS = [0, 5, 10, 30, 60, 120, 300, 600];
const DAY_MS = 24 * 60 * 60 * 1000;

export function ChannelSettingsScreen(props: {
  rt: Runtime;
  t: Dict;
  room: Room;
  onClose(): void;
}): JSX.Element {
  const rt = props.rt;
  const t = props.t;
  const s = rt.state;
  const start = props.room.settings;

  // ---- the draft ----------------------------------------------------------
  const [isPrivate, setIsPrivate] = createSignal(start.is_private);
  /** null = leave alone; "" = remove; anything else = set. */
  const [password, setPassword] = createSignal<string | null>(null);
  const [maxVoiceS, setMaxVoiceS] = createSignal(Math.round(start.max_voice_ms / 1000));
  const [maxMediaMb, setMaxMediaMb] = createSignal(
    Math.max(1, Math.round(start.max_media_bytes / (1024 * 1024))),
  );
  const [maxChars, setMaxChars] = createSignal(start.max_message_chars);
  const [slowmodeIx, setSlowmodeIx] = createSignal(
    Math.max(
      0,
      SLOWMODE_STEPS.findIndex((v) => v >= start.slowmode_s),
    ),
  );
  const [voiceEnabled, setVoiceEnabled] = createSignal(start.voice_enabled);
  const [bgBlur, setBgBlur] = createSignal(start.bg_blur);
  const [bgBrightness, setBgBrightness] = createSignal(start.bg_brightness);
  const [bgLight, setBgLight] = createSignal<BgIntent>({ t: "keep" });
  const [bgDark, setBgDark] = createSignal<BgIntent>({ t: "keep" });
  const [saving, setSaving] = createSignal(false);

  // ---- people & invite ----------------------------------------------------
  const [members, setMembers] = createSignal<ChannelMember[]>([]);
  const [bans, setBans] = createSignal<import("../gen/dto").ChannelBan[]>([]);
  const [invite, setInvite] = createSignal<Invite | null>(null);
  const [menuFor, setMenuFor] = createSignal<number | null>(null);
  // The member's menu opens over this screen, so Escape closes it before it
  // closes the screen — which is what the stack in `lib/escape` decides
  // without either of them knowing about the other.
  escapable(() => menuFor() !== null, () => setMenuFor(null));

  onMount(() => {
    const lifetime = currentLifetime();
    void api.fetchMembers(props.room.channelId).then((r) => {
      if (isCurrent(lifetime)) setMembers(r.members);
    }).catch(() => {});
    void api
      .fetchBans(props.room.channelId)
      .then((r) => { if (isCurrent(lifetime)) setBans(r.bans); })
      .catch(() => {});
    void api
      .fetchInvite(props.room.channelId)
      .then((r) => { if (isCurrent(lifetime)) setInvite(r); })
      .catch(() => { if (isCurrent(lifetime)) setInvite(null); });
  });

  const isOwner = () => props.room.ownerId === s.selfUserId || s.account.isAdmin;

  // ---- Done ---------------------------------------------------------------
  async function commit(): Promise<void> {
    const lifetime = currentLifetime();
    if (saving()) return;
    setSaving(true);
    try {
      // Backgrounds first, and only if either half moved.
      if (bgLight().t !== "keep" || bgDark().t !== "keep") {
        const resolve = async (intent: BgIntent, current: string | null) => {
          assertCurrent(lifetime);
          if (intent.t === "keep") return current;
          if (intent.t === "remove") return null;
          const done = await upload(intent.file, "photo", { fileName: intent.file.name }, () => {});
          assertCurrent(lifetime);
          return done.file_id;
        };
        const light = await resolve(bgLight(), start.bg_light_file_id ?? null);
        if (!isCurrent(lifetime)) return;
        const dark = await resolve(bgDark(), start.bg_dark_file_id ?? null);
        if (!isCurrent(lifetime)) return;
        await api.setChannelBackground(props.room.channelId, light, dark);
        if (!isCurrent(lifetime)) return;
      }

      const patch: Record<string, unknown> = {};
      if (isPrivate() !== start.is_private) patch.is_private = isPrivate();
      if (password() !== null) patch.password = password();
      if (maxVoiceS() * 1000 !== start.max_voice_ms) patch.max_voice_ms = maxVoiceS() * 1000;
      if (maxMediaMb() * 1024 * 1024 !== start.max_media_bytes) {
        patch.max_media_bytes = maxMediaMb() * 1024 * 1024;
      }
      if (maxChars() !== start.max_message_chars) patch.max_message_chars = maxChars();
      const slow = SLOWMODE_STEPS[slowmodeIx()] ?? 0;
      if (slow !== start.slowmode_s) patch.slowmode_s = slow;
      if (voiceEnabled() !== start.voice_enabled) patch.voice_enabled = voiceEnabled();
      if (bgBlur() !== start.bg_blur) patch.bg_blur = bgBlur();
      if (bgBrightness() !== start.bg_brightness) patch.bg_brightness = bgBrightness();
      if (Object.keys(patch).length > 0) {
        await api.patchChannelSettings(props.room.channelId, patch);
        if (!isCurrent(lifetime)) return;
      }
      props.onClose();
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      rt.showNotice(e instanceof Error ? e.message : String(e));
    } finally {
      if (isCurrent(lifetime)) {
        setSaving(false);
      }
    }
  }

  // ---- people actions -----------------------------------------------------
  async function act(fn: () => Promise<void>): Promise<void> {
    const lifetime = currentLifetime();
    setMenuFor(null);
    try {
      await fn();
      if (!isCurrent(lifetime)) return;
      const [m, b] = await Promise.all([
        api.fetchMembers(props.room.channelId),
        api.fetchBans(props.room.channelId).catch(() => ({ bans: [] })),
      ]);
      if (!isCurrent(lifetime)) return;
      setMembers(m.members);
      setBans(b.bans);
    } catch (e) {
      if (!isCurrent(lifetime)) return;
      rt.showNotice(e instanceof Error ? e.message : String(e));
    }
  }

  async function updateInvite(revoke = false): Promise<void> {
    const lifetime = currentLifetime();
    try {
      let next: Invite | null = null;
      if (revoke) await api.revokeInvite(props.room.channelId);
      else next = await api.createInvite(props.room.channelId);
      if (isCurrent(lifetime)) setInvite(next);
    } catch (error) {
      if (isCurrent(lifetime)) rt.showNotice(error instanceof Error ? error.message : String(error));
    }
  }

  const inviteLink = () => {
    const inv = invite();
    return inv ? `${location.origin}/join/${props.room.channelId}?token=${inv.token}` : "";
  };

  const nameOf = (id: number) => mut.nameOf(s.directory, id);

  return (
    <div class="echo-scrim echo-transition-enter" onClick={() => props.onClose()}>
      <div style="display:contents" onClick={(e) => e.stopPropagation()}>
        <div class="profile-screen surface-pane echo-material-thick admin-console admin-console--channel">
          <div class="profile-screen__header">
            <button
              type="button"
              class="echo-button echo-button--medium echo-button--borderless"
              onClick={() => props.onClose()}
            >
              <Icon name="close" size={15} />
              {t.consoleCancel()}
            </button>
            <span class="profile-screen__title">{t.consoleTitle(props.room.name)}</span>
            <button
              type="button"
              class="echo-button echo-button--medium echo-button--prominent"
              disabled={saving()}
              onClick={() => void commit()}
            >
              <Icon name="check" size={15} />
              <Show when={!saving()} fallback={<ActivityIndicator />}>
                {t.consoleDone()}
              </Show>
            </button>
          </div>
          <div class="profile-screen__body admin-console__body">
            {/* Access */}
            <div class="echo-list-section">
              <div class="echo-list-section__title">{t.consoleAccess()}</div>
              <div class="echo-list-section__card">
                <label class="echo-list-row">
                  <span class="echo-list-row__title">{t.consolePrivate()}</span>
                  <span class="layout-spacer" />
                  <input
                    type="checkbox"
                    class="echo-toggle"
                    checked={isPrivate()}
                    onChange={(e) => setIsPrivate(e.currentTarget.checked)}
                  />
                </label>
                <div class="echo-list-separator" />
                <div class="echo-list-row">
                  <span class="echo-list-row__title">{t.consolePassword()}</span>
                  <span class="layout-spacer" />
                  <input
                    class="hotkey-capture admin-console__password"
                    type="password"
                    placeholder={start.has_password ? t.consolePwKept() : t.consolePwNone()}
                    onInput={(e) => setPassword(e.currentTarget.value)}
                  />
                  <Show when={start.has_password}>
                    <button
                      type="button"
                      class="echo-button echo-button--medium echo-button--bordered echo-button--destructive"
                      onClick={() => setPassword("")}
                    >
                      <Icon name="delete" size={15} />
                      {t.consolePwRemove()}
                    </button>
                  </Show>
                </div>
              </div>
              <Show when={password() === ""}>
                <div class="echo-list-section__footer">{t.consolePwWillRemove()}</div>
              </Show>
            </div>

            {/* Limits */}
            <div class="echo-list-section">
              <div class="echo-list-section__title">{t.consoleLimits()}</div>
              <div class="echo-list-section__card">
                <SliderRow
                  label={t.consoleVoiceLimit(maxVoiceS())}
                  min={30}
                  max={300}
                  step={30}
                  value={maxVoiceS()}
                  onInput={setMaxVoiceS}
                />
                <div class="echo-list-separator" />
                <SliderRow
                  label={t.consoleMediaLimit(maxMediaMb())}
                  min={1}
                  max={100}
                  step={1}
                  value={maxMediaMb()}
                  onInput={setMaxMediaMb}
                />
                <div class="echo-list-separator" />
                <SliderRow
                  label={t.consoleCharsLimit(maxChars())}
                  min={200}
                  max={4000}
                  step={100}
                  value={maxChars()}
                  onInput={setMaxChars}
                />
                <div class="echo-list-separator" />
                <SliderRow
                  label={t.consoleSlowmode(SLOWMODE_STEPS[slowmodeIx()] ?? 0)}
                  min={0}
                  max={SLOWMODE_STEPS.length - 1}
                  step={1}
                  value={slowmodeIx()}
                  onInput={setSlowmodeIx}
                />
              </div>
            </div>

            {/* Voice */}
            <div class="echo-list-section">
              <div class="echo-list-section__card">
                <label class="echo-list-row">
                  <span class="echo-list-row__title">{t.consoleVoiceEnabled()}</span>
                  <span class="layout-spacer" />
                  <input
                    type="checkbox"
                    class="echo-toggle"
                    checked={voiceEnabled()}
                    onChange={(e) => setVoiceEnabled(e.currentTarget.checked)}
                  />
                </label>
              </div>
            </div>

            {/* Invite */}
            <div class="echo-list-section">
              <div class="echo-list-section__title">{t.consoleInvite()}</div>
              <div class="echo-list-section__card">
                <Show
                  when={invite()}
                  fallback={
                    <div class="echo-list-row">
                      <span class="echo-list-row__subtitle">{t.consoleInviteNone()}</span>
                      <span class="layout-spacer" />
                      <button
                        type="button"
                        class="echo-button echo-button--medium echo-button--secondary"
                        onClick={() =>
                          void updateInvite()
                        }
                      >
                        <Icon name="add" size={15} />
                        {t.consoleInviteCreate()}
                      </button>
                    </div>
                  }
                >
                  <div class="echo-list-row echo-list-row--auto-height admin-console__invite">
                    <span class="echo-list-row__subtitle admin-console__link">{inviteLink()}</span>
                    <span class="layout-spacer" />
                    <button
                      type="button"
                      class="echo-button echo-button--medium echo-button--secondary"
                      onClick={() => {
                        void navigator.clipboard.writeText(inviteLink());
                        rt.showNotice(t.consoleInviteCopied());
                      }}
                    >
                      <Icon name="content_copy" size={15} />
                      {t.consoleInviteCopy()}
                    </button>
                    <button
                      type="button"
                      class="echo-button echo-button--medium echo-button--secondary"
                      title={t.consoleInviteRefresh()}
                      onClick={() => void updateInvite()}
                    >
                      <Icon name="refresh" size={15} />
                    </button>
                    <button
                      type="button"
                      class="echo-button echo-button--medium echo-button--bordered echo-button--destructive"
                      onClick={() =>
                        void updateInvite(true)
                      }
                    >
                      <Icon name="delete" size={15} />
                      {t.consoleInviteRevoke()}
                    </button>
                  </div>
                </Show>
              </div>
              <div class="echo-list-section__footer">{t.consoleInviteTtl()}</div>
            </div>

            {/* Appearance */}
            <div class="echo-list-section">
              <div class="echo-list-section__title">{t.consoleAppearance()}</div>
              <div class="echo-list-section__card">
                <BgRow
                  label={t.consoleBgLight()}
                  current={start.bg_light_file_id ?? null}
                  intent={bgLight()}
                  onIntent={setBgLight}
                  t={t}
                />
                <div class="echo-list-separator" />
                <BgRow
                  label={t.consoleBgDark()}
                  current={start.bg_dark_file_id ?? null}
                  intent={bgDark()}
                  onIntent={setBgDark}
                  t={t}
                />
                <div class="echo-list-separator" />
                <SliderRow
                  label={t.consoleBgBlur(bgBlur())}
                  min={0}
                  max={100}
                  step={5}
                  value={bgBlur()}
                  onInput={setBgBlur}
                />
                <div class="echo-list-separator" />
                <SliderRow
                  label={t.consoleBgBrightness(bgBrightness())}
                  min={0}
                  max={100}
                  step={5}
                  value={bgBrightness()}
                  onInput={setBgBrightness}
                />
              </div>
            </div>

            {/* People */}
            <div class="echo-list-section">
              <div class="echo-list-section__title">{t.consolePeople()}</div>
              <div class="echo-list-section__card">
                <For each={members()}>
                  {(m) => (
                    <div class="echo-list-row admin-console__person">
                      <span class="echo-list-row__title">{nameOf(m.user_id)}</span>
                      <span class="echo-list-row__subtitle">
                        {props.room.ownerId === m.user_id
                          ? t.consoleRoleOwner()
                          : m.admin
                            ? t.consoleRoleAdmin()
                            : ""}
                      </span>
                      <span class="layout-spacer" />
                      <Show when={m.user_id !== s.selfUserId}>
                        <button
                          type="button"
                          class="echo-button echo-button--medium echo-button--secondary echo-button--icon-only"
                          onClick={() =>
                            setMenuFor(menuFor() === m.user_id ? null : m.user_id)
                          }
                        >
                          <Icon name="more_horiz" size={15} />
                        </button>
                      </Show>
                      <Show when={menuFor() === m.user_id}>
                        <div class="echo-popover admin-console__menu">
                          <div class="echo-menu" role="menu">
                            <Show when={isOwner() && props.room.ownerId !== m.user_id}>
                              <button
                                type="button"
                                class="echo-menu__item"
                                onClick={() =>
                                  void act(() =>
                                    api.setMemberAdmin(
                                      props.room.channelId,
                                      m.user_id,
                                      !m.admin,
                                    ),
                                  )
                                }
                              >
                                <Icon name={m.admin ? "arrow_downward" : "arrow_upward"} size={18} />
                                {m.admin ? t.consoleRevokeAdmin() : t.consoleMakeAdmin()}
                              </button>
                              <button
                                type="button"
                                class="echo-menu__item"
                                onClick={() =>
                                  void act(() =>
                                    api.transferOwner(props.room.channelId, m.user_id),
                                  )
                                }
                              >
                                <Icon name="group" size={18} />
                                {t.consoleMakeOwner()}
                              </button>
                            </Show>
                            <button
                              type="button"
                              class="echo-menu__item"
                              onClick={() =>
                                void act(() =>
                                  api.voiceMuteMember(props.room.channelId, m.user_id),
                                )
                              }
                            >
                              <Icon name="mic_off" size={18} />
                              {t.consoleVoiceMute()}
                            </button>
                            <button
                              type="button"
                              class="echo-menu__item"
                              onClick={() =>
                                void act(() =>
                                  api.voiceUnmuteMember(props.room.channelId, m.user_id),
                                )
                              }
                            >
                              <Icon name="mic" size={18} />
                              {t.consoleVoiceUnmute()}
                            </button>
                            <button
                              type="button"
                              class="echo-menu__item"
                              onClick={() =>
                                void act(() => api.removeMember(props.room.channelId, m.user_id))
                              }
                            >
                              <Icon name="logout" size={18} />
                              {t.consoleKick()}
                            </button>
                            <button
                              type="button"
                              class="echo-menu__item echo-menu__item--destructive"
                              onClick={() =>
                                void act(() =>
                                  api.banMember(
                                    props.room.channelId,
                                    m.user_id,
                                    Date.now() + DAY_MS,
                                  ),
                                )
                              }
                            >
                              <Icon name="highlight_off" size={18} />
                              {t.consoleBanDay()}
                            </button>
                            <button
                              type="button"
                              class="echo-menu__item echo-menu__item--destructive"
                              onClick={() =>
                                void act(() => api.banMember(props.room.channelId, m.user_id))
                              }
                            >
                              <Icon name="highlight_off" size={18} />
                              {t.consoleBanForever()}
                            </button>
                          </div>
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
              <Show when={bans().length > 0}>
                <div class="echo-list-section__title">{t.consoleBanned()}</div>
                <div class="echo-list-section__card">
                  <For each={bans()}>
                    {(b) => (
                      <div class="echo-list-row">
                        <span class="echo-list-row__title">{nameOf(b.user_id)}</span>
                        <span class="echo-list-row__subtitle">
                          {b.until_ms
                            ? t.consoleBanUntil(new Date(b.until_ms).toLocaleString())
                            : t.consoleBanPermanent()}
                        </span>
                        <span class="layout-spacer" />
                        <button
                          type="button"
                          class="echo-button echo-button--medium echo-button--secondary"
                          onClick={() =>
                            void act(() => api.unbanMember(props.room.channelId, b.user_id))
                          }
                        >
                          <Icon name="check" size={15} />
                          {t.consoleUnban()}
                        </button>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderRow(props: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onInput(v: number): void;
}): JSX.Element {
  return (
    <div class="echo-list-row echo-list-row--tall admin-console__slider">
      <span class="echo-list-row__title">{props.label}</span>
      <input
        class="echo-slider"
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        onInput={(e) => props.onInput(Number(e.currentTarget.value))}
      />
    </div>
  );
}

function BgRow(props: {
  label: string;
  current: string | null;
  intent: BgIntent;
  onIntent(i: BgIntent): void;
  t: Dict;
}): JSX.Element {
  let input: HTMLInputElement | undefined;
  const stateText = () => {
    const i = props.intent;
    if (i.t === "pick") return i.file.name;
    if (i.t === "remove") return props.t.consoleBgWillRemove();
    return props.current ? props.t.consoleBgSet() : props.t.consoleBgNone();
  };
  return (
    <div class="echo-list-row">
      <span class="echo-list-row__title">{props.label}</span>
      <span class="echo-list-row__subtitle admin-console__background-state">{stateText()}</span>
      <span class="layout-spacer" />
      <input
        ref={input}
        type="file"
        accept="image/*"
        style="display:none"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) props.onIntent({ t: "pick", file });
          e.currentTarget.value = "";
        }}
      />
      <button
        type="button"
        class="echo-button echo-button--medium echo-button--secondary"
        onClick={() => input?.click()}
      >
        <Icon name="upload" size={15} />
        {props.t.consoleBgPick()}
      </button>
      <Show when={props.current !== null || props.intent.t === "pick"}>
        <button
          type="button"
          class="echo-button echo-button--medium echo-button--bordered echo-button--destructive"
          onClick={() => props.onIntent({ t: "remove" })}
        >
          <Icon name="delete" size={15} />
          {props.t.consoleBgRemove()}
        </button>
      </Show>
    </div>
  );
}
